import { deleteData, getData, postData, putData } from "../api/api";

/**
 * Super Admin: the Free plan, the support catalog, and per-workspace custom quotas and support packages.
 * The product is free; quotas apply only while a workspace uses the BEAS LLM / BEAS store.
 */

export type QuotaValues = {
  maxWorkspaceUsers?: number | null;
  aiAnalysisMaxChars?: number | null;
  guideStorageMaxBytes?: number | null;
  maxSingleGuideBytes?: number | null;
  maxDataAdaptors?: number | null;
  maxPrepJobs?: number | null;
  maxOpenApiCatalogs?: number | null;
  maxIngestSettingsItemsPerTab?: number | null;
  maxWidgets?: number | null;
  maxDashboards?: number | null;
  maxDashboardQueries?: number | null;
  maxInsightReportJobs?: number | null;
  beasOpenSearchQuotaBytes?: number | null;
  beasOpenSearchMaxIndices?: number | null;
  maxEmbeds?: number | null;
};

export type QuotaKey = keyof QuotaValues;

/** Which BEAS resource a quota belongs to: own LLM lifts "llm", own OpenSearch lifts "store", own both lift "count". */
export type QuotaGroup = "llm" | "store" | "count";

export const QUOTA_FIELDS: { key: QuotaKey; group: QuotaGroup; unit?: "mb" }[] = [
  { key: "aiAnalysisMaxChars", group: "llm" },
  { key: "beasOpenSearchQuotaBytes", group: "store", unit: "mb" },
  { key: "beasOpenSearchMaxIndices", group: "store" },
  { key: "guideStorageMaxBytes", group: "store", unit: "mb" },
  { key: "maxSingleGuideBytes", group: "store", unit: "mb" },
  { key: "maxWorkspaceUsers", group: "count" },
  { key: "maxDataAdaptors", group: "count" },
  { key: "maxPrepJobs", group: "count" },
  { key: "maxOpenApiCatalogs", group: "count" },
  { key: "maxIngestSettingsItemsPerTab", group: "count" },
  { key: "maxWidgets", group: "count" },
  { key: "maxDashboards", group: "count" },
  { key: "maxDashboardQueries", group: "count" },
  { key: "maxInsightReportJobs", group: "count" },
  { key: "maxEmbeds", group: "count" },
];

export type FreePlan = {
  quotas: QuotaValues;
  includesBeasLlm: boolean;
  note?: string | null;
  lastModifiedDate?: string | null;
  lastModifiedBy?: string | null;
};

export type SupportPackage = {
  key: string;
  name: string;
  description?: string | null;
  priceEur?: number | null;
  installFeeEur?: number | null;
  tickets?: number | null;
  meetingHours?: number | null;
  ticketResponse?: string | null;
  meetingResponse?: string | null;
  termMonths: number;
  listed: boolean;
  active: boolean;
  sortOrder: number;
};

export type SupportPackagePayload = Omit<SupportPackage, "key" | "sortOrder">;

export type ManualPayment = {
  id?: string;
  invoiceNo: string;
  amount: number;
  currency?: string;
  paidAt?: string;
  recordedBy?: string;
};

export type SupportUsage = {
  id: string;
  kind: "TICKET" | "MEETING";
  amount: number;
  reference?: string | null;
  note?: string | null;
  at: string;
  recordedBy?: string | null;
};

export type SupportEntitlement = {
  id: string;
  workspaceId: string;
  packageKey: string;
  packageName: string;
  tickets?: number | null;
  ticketsUsed: number;
  ticketsRemaining?: number | null;
  meetingHours?: number | null;
  meetingMinutesUsed: number;
  meetingMinutesRemaining?: number | null;
  ticketResponse?: string | null;
  meetingResponse?: string | null;
  priceEur?: number | null;
  installFeeEur?: number | null;
  startsAt: string;
  expiresAt: string;
  status: "ACTIVE" | "EXPIRED" | "CANCELLED";
  usable: boolean;
  note?: string | null;
  payments?: ManualPayment[] | null;
  usage?: SupportUsage[] | null;
};

export type SupportAssignPayload = {
  packageKey: string;
  startsAt?: string | null;
  note?: string | null;
  name?: string | null;
  tickets?: number | null;
  meetingHours?: number | null;
  priceEur?: number | null;
  installFeeEur?: number | null;
  ticketResponse?: string | null;
  meetingResponse?: string | null;
  termMonths?: number | null;
};

export type QuotaOverride = {
  workspaceId: string;
  unlimited: boolean;
  quotas: QuotaValues;
  validFrom?: string | null;
  validUntil?: string | null;
  active: boolean;
  note?: string | null;
  source?: string | null;
  lastModifiedDate?: string | null;
  lastModifiedBy?: string | null;
};

export type QuotaOverridePayload = {
  unlimited: boolean;
  quotas: QuotaValues;
  validFrom?: string | null;
  validUntil?: string | null;
  note?: string | null;
};

export type CustomerSummary = {
  workspaceId: string;
  name: string;
  slug?: string;
  ownerUsername?: string;
  createdDate?: string;
  llmMode?: "BEAS_MANAGED" | "EXTERNAL" | null;
  openSearchMode?: "BEAS_MANAGED" | "EXTERNAL" | null;
  beasLlmInUse: boolean;
  beasStoreInUse: boolean;
  customQuota: boolean;
  customQuotaActive: boolean;
  customQuotaUnlimited: boolean;
  customQuotaUntil?: string | null;
  activeSupport: SupportEntitlement[];
};

export type LegacySubscription = {
  id: string;
  username?: string;
  email?: string;
  customerType: string;
  paymentStatus: string;
  activationDate?: string;
  deactivationDate?: string;
  extendedWaitUntil?: string;
  active: boolean;
  migratedAt?: string | null;
  payments?: ManualPayment[];
};

export type CustomerDetail = {
  customer: CustomerSummary;
  quotaSource: "free" | "custom" | "unlimited";
  effectiveQuotas: QuotaValues;
  freeQuotas: QuotaValues;
  customQuota?: QuotaOverride | null;
  support: SupportEntitlement[];
  legacySubscriptions: LegacySubscription[];
};

export type CustomerPage = { items: CustomerSummary[]; total: number; page: number; size: number };

const BASE = "/admin";
const customer = (id: string) => `${BASE}/customers/${encodeURIComponent(id)}`;

export const customerPlanService = {
  getFreePlan: () => getData(`${BASE}/plan`) as Promise<FreePlan>,
  updateFreePlan: (payload: { quotas: QuotaValues; includesBeasLlm: boolean; note?: string | null }) =>
    putData(`${BASE}/plan`, payload) as Promise<FreePlan>,
  listSupportPackages: () => getData(`${BASE}/support-packages`) as Promise<SupportPackage[]>,
  updateSupportPackage: (key: string, payload: SupportPackagePayload) =>
    putData(`${BASE}/support-packages/${encodeURIComponent(key)}`, payload) as Promise<SupportPackage>,
  listCustomers: (search: string, page = 0, size = 25) => {
    const params = new URLSearchParams({ page: String(page), size: String(size) });
    if (search.trim()) params.set("search", search.trim());
    return getData(`${BASE}/customers?${params}`) as Promise<CustomerPage>;
  },
  getCustomer: (workspaceId: string) => getData(customer(workspaceId)) as Promise<CustomerDetail>,
  setQuotaOverride: (workspaceId: string, payload: QuotaOverridePayload) =>
    putData(`${customer(workspaceId)}/quota-override`, payload) as Promise<QuotaOverride>,
  removeQuotaOverride: (workspaceId: string) =>
    deleteData(`${customer(workspaceId)}/quota-override`) as Promise<void>,
  assignSupport: (workspaceId: string, payload: SupportAssignPayload) =>
    postData(`${customer(workspaceId)}/support`, payload) as Promise<SupportEntitlement>,
  recordSupportPayment: (workspaceId: string, id: string, invoiceNo: string, amount: number, currency = "EUR") =>
    postData(`${customer(workspaceId)}/support/${encodeURIComponent(id)}/payments`, {
      invoiceNo,
      amount,
      currency,
    }) as Promise<SupportEntitlement>,
  recordSupportUsage: (
    workspaceId: string,
    id: string,
    payload: { kind: "TICKET" | "MEETING"; amount: number; reference?: string; note?: string }
  ) => postData(`${customer(workspaceId)}/support/${encodeURIComponent(id)}/usage`, payload) as Promise<SupportEntitlement>,
  cancelSupport: (workspaceId: string, id: string) =>
    postData(`${customer(workspaceId)}/support/${encodeURIComponent(id)}/cancel`, {}) as Promise<SupportEntitlement>,
};

/** Workspace side: support packages of the current workspace (workspace admins). */
export const workspaceSupportService = {
  list: () => getData("/workspaces/me/support") as Promise<SupportEntitlement[]>,
};

export const MB = 1024 * 1024;
